import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'; // Import Navigate
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, onValue, set, get, update } from 'firebase/database';
import { auth, database } from './firebase/firebaseConfig';
import { updateAccount, restoreAccount, ensureAutomaticBackup } from './firebase/firebaseUtils';
import Header from './components/Header';
import Overview from './components/Overview';
import Login from './components/Login';
import UserSettingsModal from './components/UserSettingsModal';
import BirthDateModal from './components/BirthDateModal';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ThemeContext } from './context/ThemeContext';
import { GlobalStyle, MainContent } from './components/StyledComponents';
import Accounts from './components/Accounts';
import Transactions from './components/Transactions';
import BugReportModal from './components/BugReportModal';
import AdminPage from './components/AdminPage';
import ArchivedAccountsModal from './components/ArchivedAccountsModal';

function App() {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState({}); // Define accounts state
  const [settings, setSettings] = useState({
    theme: 'dark',
    totalLineColor: '#000000',
    showTotalLine: true,
    enabledAccounts: {},
    transactionsPageSize: 30,
    expenseAccountFilter: {},
    incomeAccountFilter: {},
    incomeExpenseAccountFilter: {},
    cashflowAccountFilter: {},
    cashflowPeriod: 3,
    cashShortfallAccountFilter: {},
    balanceAccountFilter: {},
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [birthDate, setBirthDate] = useState(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isBirthDateModalOpen, setIsBirthDateModalOpen] = useState(false);
  const { theme, updateTheme } = useContext(ThemeContext);
  const [customizeWidgetsHandler, setCustomizeWidgetsHandler] = useState(null);
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [bugContext, setBugContext] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isArchivedAccountsModalOpen, setIsArchivedAccountsModalOpen] = useState(false);

  const archivedAccounts = useMemo(
    () => Object.entries(accounts || {}).filter(([, account]) => account?.archived),
    [accounts]
  );
 
  // Monitor auth state
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    // Cleanup function to unsubscribe from the auth listener on component unmount
    return unsubscribeAuth;
  }, []);

  useEffect(() => {
    setSettingsLoaded(false);
    if (user) {
      const settingsRef = ref(database, `users/${user.uid}/settings`);
      onValue(settingsRef, (snapshot) => {
        if (snapshot.exists()) {
          const userSettings = snapshot.val();
          updateTheme(userSettings.theme === 'light' ? 'light' : 'dark');
          setSettings({
            theme: userSettings.theme || 'dark',
            totalLineColor: userSettings.totalLineColor || '#000000',
            showTotalLine: userSettings.showTotalLine !== false,
            enabledAccounts: userSettings.enabledAccounts || {},
            transactionsPageSize: userSettings.transactionsPageSize || 30,
            expenseAccountFilter: userSettings.expenseAccountFilter || {},
            incomeAccountFilter: userSettings.incomeAccountFilter || {},
            incomeExpenseAccountFilter: userSettings.incomeExpenseAccountFilter || {},
            cashflowAccountFilter: userSettings.cashflowAccountFilter || {},
            cashflowPeriod: userSettings.cashflowPeriod || 3,
            cashShortfallAccountFilter: userSettings.cashShortfallAccountFilter || {},
            balanceAccountFilter: userSettings.balanceAccountFilter || {},
          });
          setSettingsLoaded(true);
        } else {
          const defaultSettings = {
            theme: 'dark',
            totalLineColor: '#000000',
            showTotalLine: true,
            enabledAccounts: {},
            transactionsPageSize: 30,
            expenseAccountFilter: {},
            incomeAccountFilter: {},
            incomeExpenseAccountFilter: {},
            cashflowAccountFilter: {},
            cashflowPeriod: 3,
            cashShortfallAccountFilter: {},
            balanceAccountFilter: {},
          };
          set(settingsRef, defaultSettings)
            .catch((error) => {
              console.error('Failed to seed default settings', error);
            })
            .finally(() => {
              setSettingsLoaded(true);
            });
          updateTheme('dark');
          setSettings(defaultSettings);
        }
      }, {
        onlyOnce: true
      });
    }
  }, [user, updateTheme]);

  useEffect(() => {
    if (user) {
      const profileRef = ref(database, `users/${user.uid}/profile`);
      get(profileRef).then((snapshot) => {
        const data = snapshot.val() || {};
        const updates = {};

        if (!data.email && user.email) {
          updates.email = user.email;
        }

        if (!data.displayName && user.displayName) {
          updates.displayName = user.displayName;
        }

        if (!data.createdAt && user.metadata?.creationTime) {
          updates.createdAt = user.metadata.creationTime;
        }

        if (data.birthDate) {
          setBirthDate(data.birthDate);
        } else {
          if (user.metadata.creationTime === user.metadata.lastSignInTime) {
            setIsBirthDateModalOpen(true);
          } else {
            updates.birthDate = '1975-08-28';
            setBirthDate('1975-08-28');
          }
        }

        if (Object.keys(updates).length > 0) {
          update(profileRef, updates).catch((error) => {
            console.error('Failed to update profile defaults', error);
          });
        }

        const directoryRef = ref(database, `userDirectory/${user.uid}`);
        const directoryPayload = {
          displayName: updates.displayName || data.displayName || user.displayName || 'Unknown user',
          email: updates.email || data.email || user.email || 'Email not stored',
          createdAt: updates.createdAt || data.createdAt || user.metadata?.creationTime || new Date().toISOString(),
          lastLogin: user.metadata?.lastSignInTime || new Date().toISOString(),
        };

        update(directoryRef, directoryPayload).catch((error) => {
          console.error('Failed to update user directory entry', error);
        });
      });
    } else {
      setBirthDate(null);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const accountsRef = ref(database, `users/${user.uid}/accounts`);
      onValue(accountsRef, (snapshot) => {
        const data = snapshot.val();
        const formattedAccounts = data ? Object.entries(data).reduce((acc, [key, value]) => {
          if (!value.color) {
            const randomColor = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
            updateAccount(user.uid, key, { color: randomColor });
            value.color = randomColor;
          }
          acc[key] = value;
          return acc;
        }, {}) : {};
        setAccounts(formattedAccounts);
      });
    } else {
      setAccounts({});
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    const adminRef = ref(database, `admins/${user.uid}`);
    const adminsRootRef = ref(database, 'admins');
    const bootstrapEmails = (process.env.REACT_APP_BOOTSTRAP_ADMIN_EMAILS || '')
      .split(',')
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);

    const ensureAdmin = async () => {
      try {
        if (bootstrapEmails.length > 0) {
          if (user.email && bootstrapEmails.includes(user.email.toLowerCase())) {
            await set(adminRef, true);
            await update(ref(database, `userDirectory/${user.uid}`), { role: 'admin' });
          }
        } else {
          const existingAdmins = await get(adminsRootRef);
          if (!existingAdmins.exists()) {
            await set(adminRef, true);
            await update(ref(database, `userDirectory/${user.uid}`), { role: 'admin' });
          }
        }
      } catch (error) {
        console.error('Failed to grant admin role', error);
      }
    };

    ensureAdmin();

    const unsubscribe = onValue(adminRef, (snapshot) => {
      const isCurrentAdmin = Boolean(snapshot.val());
      setIsAdmin(isCurrentAdmin);
      update(ref(database, `userDirectory/${user.uid}`), { role: isCurrentAdmin ? 'admin' : 'user' }).catch((error) => {
        console.error('Failed to sync directory role', error);
      });
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!user?.uid) {
      return;
    }
    ensureAutomaticBackup(user.uid).catch((error) => {
      console.error('Failed to run automatic backup', error);
    });
  }, [user?.uid]);

  const toggleSettingsModal = () => setIsSettingsModalOpen(!isSettingsModalOpen);

  const openArchivedAccountsModal = () => {
    setIsArchivedAccountsModalOpen(true);
  };

  const handleRestoreArchivedAccount = async (accountId) => {
    const userId = user?.uid;
    if (!userId) return;
    try {
      await restoreAccount(userId, accountId);
    } catch (error) {
      console.error('Error restoring account: ', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const handleSettingsSave = (newSettings, newBirthDate) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    if (newBirthDate) {
      setBirthDate(newBirthDate);
    }
    updateTheme(newSettings.theme === 'light' ? 'light' : 'dark');
  };

  const handleSettingsUpdate = useCallback((partialSettings) => {
    if (!partialSettings || typeof partialSettings !== 'object') {
      return;
    }
    setSettings((prev) => ({ ...prev, ...partialSettings }));
  }, []);

  const handleBirthDateSave = (date) => {
    if (user) {
      const profileRef = ref(database, `users/${user.uid}/profile`);
      set(profileRef, { birthDate: date }).then(() => {
        setBirthDate(date);
        setIsBirthDateModalOpen(false);
      });
    }
  };

  const handleReportBug = () => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    setBugContext({
      path,
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || 'unknown',
      accountsCount: Object.keys(accounts || {}).length,
      theme: settings?.theme,
      timestamp: new Date().toISOString(),
    });
    setIsBugModalOpen(true);
  };

  return (
    <StyledThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <div className="App">
          <Header
            user={user}
            isAdmin={isAdmin}
            onLogout={handleLogout}
            onSettings={toggleSettingsModal}
            onCustomizeWidgets={customizeWidgetsHandler}
            onReportBug={handleReportBug}
          />
          {isBirthDateModalOpen && (
            <BirthDateModal isOpen={isBirthDateModalOpen} onSave={handleBirthDateSave} />
          )}
          {isBugModalOpen && (
            <BugReportModal
              isOpen={isBugModalOpen}
              onClose={() => setIsBugModalOpen(false)}
              context={bugContext}
            />
          )}
          <MainContent>
            {user ? (
              <Routes>
                <Route
                  path="/overview"
                  element={(
                    <Overview
                      userId={user?.uid}
                      settings={settings}
                      settingsLoaded={settingsLoaded}
                      onSettingsUpdate={handleSettingsUpdate}
                      setCustomizeWidgetsHandler={setCustomizeWidgetsHandler}
                      isAdmin={isAdmin}
                      birthDate={birthDate}
                    />
                  )}
                />
                <Route path="/accounts" element={<Accounts userId={user?.uid} accounts={accounts} setAccounts={setAccounts} setCustomizeWidgetsHandler={setCustomizeWidgetsHandler} />} />
                <Route path="/transactions" element={<Transactions userId={user?.uid} accounts={accounts} birthDate={birthDate} settings={settings} />} />
                <Route path="/admin" element={isAdmin ? <AdminPage currentUser={user} /> : <Navigate to="/overview" />} />
                <Route path="/" element={<Navigate to="/overview" />} />
                <Route path="*" element={<Navigate to="/overview" />} />
              </Routes>
            ) : (
              <Login />
            )}
            {isSettingsModalOpen && (
              <UserSettingsModal
                user={user}
                isOpen={isSettingsModalOpen}
                onClose={toggleSettingsModal}
                onSave={handleSettingsSave}
                settings={settings}
                birthDate={birthDate}
                onManageArchivedAccounts={() => {
                  openArchivedAccountsModal();
                }}
              />
            )}
            {isArchivedAccountsModalOpen && (
              <ArchivedAccountsModal
                isOpen={isArchivedAccountsModalOpen}
                onClose={() => setIsArchivedAccountsModalOpen(false)}
                accounts={archivedAccounts}
                onRestore={handleRestoreArchivedAccount}
              />
            )}
          </MainContent>
        </div>
      </Router>
    </StyledThemeProvider>
  );
}

export default App;
